import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GOOGLE_SIGN_IN_MUTATION } from 'shared/components/AuthModal/section/SignIn/queries/googleSignIn';
import { triggerSuccessfulAuthentication } from 'shared/components/AuthModal/utils/triggerSuccessfulAuthentication';
import { SignInAction } from 'shared/components/AuthModal/section/SignIn/hooks/useSocialSignIn/useSocialSignIn.types';
import { loadGoogleSdk } from './loadGoogleSdk';
import {
    GoogleMutation,
    WindowWithGoogleAuth2
} from 'shared/components/AuthModal/section/SignIn/hooks/useSocialSignIn/type/useGoogle.types';
import triggerTracking from '@blueprint/tracking';
import { isNewUser } from 'shared/components/AuthModal/utils';
import { User } from 'graphql.generated';
import { USER_QUERY } from 'queries/user/user.query';
import { AuthModalTracking } from 'shared/components/AuthModal/AuthModal.types';
import { authTaxonomyObject } from 'shared/components/AuthModal/constants/taxonomy';
import { trackAuthSegmentEvent } from 'shared/components/AuthModal/utils/segmentTrack';

export const useGoogle = (
    { onSuccess, onError, eventLabel, segment }: SignInAction,
    currentUser: User = {}
) => {
    const [isGoogleSDKLoaded, setIsGoogleSDKLoaded] = useState(false);
    const [isPrivateMode, setIsPrivateMode] = useState(false);
    const [isGoogleAuthenticating, setIsGoogleAuthenticating] = useState(false);

    const [triggerSocialUserTracking] = useLazyQuery<{ user: User }>(
        USER_QUERY,
        {
            onCompleted(data) {
                if (data?.user?.registrationSources) {
                    const isGoogleOnly =
                        data?.user?.registrationSources.length === 1 &&
                        data?.user?.registrationSources[0] === 'google';

                    triggerTracking<AuthModalTracking>({
                        event: 'GAEvent',
                        eventCategory:
                            isGoogleOnly && isNewUser(currentUser, data?.user)
                                ? 'registration'
                                : 'signin',
                        eventAction: 'success - google',
                        eventLabel: eventLabel || 'sign_in_modal',
                        taxonomy: authTaxonomyObject.google.signin
                    });

                    trackAuthSegmentEvent(
                        isGoogleOnly && isNewUser(currentUser, data?.user)
                            ? 'reg'
                            : 'signin',
                        segment,
                        data?.user
                    );
                }
                // put onSuccess() here as it unmounts the modal component, caused error: `Can't perform a React state update on an unmounted component`
                onSuccess(data?.user);
            },
            onError() {
                // The user query starts when sign in mutation is completed successfully.
                // User is already logged in at this moment. Trigger onSuccess here to close the authModal.
                onSuccess();
            },
            // force the request to be fulfilled by the server, as it already ran once before
            fetchPolicy: 'network-only'
        }
    );

    const [signInMutation] = useMutation<GoogleMutation>(
        GOOGLE_SIGN_IN_MUTATION,
        {
            onCompleted({ socialSignInGoogle: { success } }) {
                if (!success) {
                    onError('Sorry, something went wrong. Please try again.');
                    setIsGoogleAuthenticating(false);
                    triggerTracking({
                        event: 'GAEvent',
                        eventCategory: 'signin',
                        eventAction: 'failure_generic',
                        eventLabel: eventLabel || 'sign_in_modal'
                    });
                    return;
                }

                triggerSuccessfulAuthentication();
                triggerSocialUserTracking();
                setIsGoogleAuthenticating(false);
            },
            onError() {
                onError('Sorry, something went wrong. Please try again.');
                setIsGoogleAuthenticating(false);
                triggerTracking({
                    event: 'GAEvent',
                    eventCategory: 'signin',
                    eventAction: 'failure_generic',
                    eventLabel: eventLabel || 'sign_in_modal'
                });
            }
        }
    );

    const setGoogleAsyncInit = () => {
        (window as WindowWithGoogleAuth2).init = () => {
            // initialise first to prevent popup being blocked by browser
            gapi.load('auth2', () => {
                gapi.auth2
                    .init({
                        client_id:
                            '696203622959-7kq1qaikjuk02838oc5ckv0t3a4329tq.apps.googleusercontent.com',
                        cookie_policy: 'single_host_origin',
                        fetch_basic_profile: true,
                        scope: 'profile',
                        ux_mode: 'popup'
                    })
                    .then(() => {
                        setIsGoogleSDKLoaded(true);
                        setIsPrivateMode(false);
                    }, onGoogleSigninError);
            });
        };
    };

    const init = () => {
        setGoogleAsyncInit();
        loadGoogleSdk();
    };

    const onGoogleSigninError = (err: { error: string; details: string }) => {
        const isCurrentPrivateMode =
            err.error === 'idpiframe_initialization_failed' &&
            err.details === 'Cookies are not enabled in current environment.';

        const errorMap: Record<string, string> = {
            popup_closed_by_user:
                'Something went wrong because the window was closed too soon. Please try again if you would like to continue.'
        };

        if (isCurrentPrivateMode) {
            setIsPrivateMode(true);
            return;
        }

        if (errorMap[err.error]) {
            onError(errorMap[err.error]);
        } else {
            onError(
                'Something went wrong and you have not been signed in. Please try again.'
            );
        }

        setIsGoogleAuthenticating(false);

        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'signin',
            eventAction: 'failure_google',
            eventLabel: eventLabel || 'sign_in_modal'
        });
    };

    const signInToZooplaWithGoogle = (
        GoogleAuth: gapi.auth2.GoogleAuth
    ): void => {
        GoogleAuth.signIn({
            scope: 'profile email',
            prompt: 'consent'
        }).then((GoogleUser) => {
            const { access_token } = GoogleUser.getAuthResponse();

            if (access_token) {
                signInMutation({
                    variables: {
                        accessToken: access_token
                    }
                });
            }
        }, onGoogleSigninError);
    };

    const onLogin = () => {
        setIsGoogleAuthenticating(true);

        if (!isGoogleSDKLoaded) {
            init();
        }

        if (isPrivateMode) {
            onError(
                'We are unable to sign you in with a social account when browsing in a private mode.'
            );
            setIsGoogleAuthenticating(false);

            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'signin',
                eventAction: 'failure_google',
                eventLabel: eventLabel || 'sign_in_modal'
            });

            return;
        }

        gapi.auth2
            .getAuthInstance()
            .then(signInToZooplaWithGoogle, onGoogleSigninError);
    };

    return {
        init,
        onLogin,
        isAuthenticating: isGoogleAuthenticating
    };
};
